import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";

import classes from "./sampleTemplatesModal.module.css";
import { SampleTemplateOptions } from "../../../constants/templateConsts";
import { debounce, has, isEmpty, sortBy, values } from "lodash";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faCheckCircle,
  faFile,
  faFileAlt,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import { compose } from "@reduxjs/toolkit";
import withLoadData from "./withLoadData";
import { connect } from "react-redux";
import { TEMPLATE_PDF_ACTIONS } from "../../../constants/app";

const SampleTemplatesModal = ({
  isOpen,
  handleClose,
  handleClickLoadSample,
  handleCreateNewTemplate,
  isCustomFile,
  hasTemplate,
  isTemplateModified,
}) => {
  const DATA = sortBy(SampleTemplateOptions, (item) =>
    item.label.toLowerCase()
  );
  const [currentList, setCurrentList] = useState(DATA);
  const [checkedItem, setCheckedItem] = useState(null);
  const [checkedNewTemplate, setCheckedNewTemplate] = useState(false);
  const [isNextStep, setIsNextStep] = useState(false);
  const [currentTemplateAction, setCurrentTemplateAction] = useState(
    isCustomFile && hasTemplate && !isTemplateModified
      ? TEMPLATE_PDF_ACTIONS.LOAD_FOR_THIS_TEMPLATE.value
      : TEMPLATE_PDF_ACTIONS.LOAD_CURRENTLY_PDF.value
  );
  const [currentFile, setCurrentFile] = useState(null);

  const uploadRef = useRef(null);

  const onSearch = debounce((text) => {
    const data = DATA.filter((item) =>
      item.label.toLowerCase().includes(text.toString().toLowerCase())
    );
    setCurrentList(data);
  }, 300);

  const onProceed = () => {
    if (checkedNewTemplate) {
      if (isCustomFile) {
        handleCreateNewTemplate(currentTemplateAction, currentFile);
      } else {
        handleCreateNewTemplate(
          TEMPLATE_PDF_ACTIONS.LOAD_FOR_THIS_TEMPLATE.value,
          null
        );
      }
    } else {
      if (isCustomFile) {
        handleClickLoadSample(checkedItem, currentTemplateAction, currentFile);
      } else {
        handleClickLoadSample(
          checkedItem,
          TEMPLATE_PDF_ACTIONS.LOAD_FOR_THIS_TEMPLATE.value,
          null
        );
      }
    }
    handleClose();
  };

  const goToNextStep = () => {
    setIsNextStep(true);
  };

  const handleChooseNewTemplate = () => {
    setCheckedNewTemplate(true);
    setCheckedItem(null);
  };

  const handleChooseItem = (item) => {
    setCheckedNewTemplate(false);
    setCheckedItem(
      currentList.find((template) => template.label === item.label)
    );
  };

  const isCheckedItem = useCallback(
    (currentItem) => {
      if (checkedItem) {
        return currentItem.label === checkedItem.label;
      }
      return false;
    },
    [checkedItem]
  );

  const handleChangeTemplateAction = (value) => {
    if (value === TEMPLATE_PDF_ACTIONS.LOAD_PDF_FROM_FILE.value) {
      uploadRef.current.click();
    } else {
      setCurrentTemplateAction(value);
      setCurrentFile(null);
    }
  };

  const handleUploadedFile = (files) => {
    if (Boolean(files.length)) {
      setCurrentFile(files);
      setCurrentTemplateAction(TEMPLATE_PDF_ACTIONS.LOAD_PDF_FROM_FILE.value);
    }
  };

  const templatePDFActions = useMemo(() => {
    if (checkedNewTemplate) {
      return values(TEMPLATE_PDF_ACTIONS).filter(
        (item) =>
          item.value !== TEMPLATE_PDF_ACTIONS.LOAD_FOR_THIS_TEMPLATE.value
      );
    }
    return values(TEMPLATE_PDF_ACTIONS);
  }, [checkedNewTemplate]);

  const handleCancel = () => {
    setCurrentFile(null);
    if (isNextStep) {
      if (checkedNewTemplate) {
        handleCreateNewTemplate(
          TEMPLATE_PDF_ACTIONS.LOAD_CURRENTLY_PDF.value,
          currentFile
        );
      } else {
        handleClickLoadSample(
          checkedItem,
          TEMPLATE_PDF_ACTIONS.LOAD_CURRENTLY_PDF.value,
          currentFile
        );
      }
      handleClose();
    } else {
      handleClose();
    }
  };

  const handleOnDoubleClickItem = (item) => {
    if (isNextStep) {
      setCurrentTemplateAction(item);
      setCurrentFile(null);
      onProceed();
    } else {
      setCheckedNewTemplate(false);
      setCheckedItem(
        currentList.find((template) => template.label === item.label)
      );
      if (!isCustomFile) {
        handleClickLoadSample(
          checkedItem,
          TEMPLATE_PDF_ACTIONS.LOAD_FOR_THIS_TEMPLATE.value,
          null
        );
        handleClose();
      } else {
        if (isCustomFile && hasTemplate && !isTemplateModified) {
          onProceed();
        } else {
          goToNextStep();
        }
      }
    }
  };

  const handleOnDoubleClickNewTemplateItem = () => {
    if (!isCustomFile) {
      handleCreateNewTemplate(
        TEMPLATE_PDF_ACTIONS.LOAD_CURRENTLY_PDF.value,
        null
      );
      handleClose();
    } else {
      setCheckedNewTemplate(true);
      setCheckedItem(null);
      goToNextStep();
    }
  };

  const isLastStep = useMemo(() => {
    return isNextStep || !isCustomFile || (isCustomFile && hasTemplate && !isTemplateModified);
  }, [isNextStep, checkedNewTemplate, isCustomFile]);

  return (
    <>
      <Modal
        // trying to fix issue with modal appearing behind some existing elements
        style={{ overlay: { zIndex: 999 }, opacity: 1 }}
        animation={false}
        // end of the fix
        show={isOpen}
        onHide={handleClose}
        size="lg"
      >
        <Modal.Header closeButton className={classes.ModalHeader}>
          <Modal.Title>Create or Open Template..</Modal.Title>
        </Modal.Header>

        <Modal.Body className={classes.Body}>
          {isNextStep ? (
            <>
              <div className={classes.SubTitle}>
                Which PDF document to use for this template?
              </div>

              <div className={classNames(classes.ListWrap, classes.NextList)}>
                {templatePDFActions.map((item) => (
                  <div
                    key={item.value}
                    className={classNames(classes.TemplateItem, {
                      [classes.ItemChecked]:
                        currentTemplateAction === item.value,
                    })}
                    onClick={() => handleChangeTemplateAction(item.value)}
                    onDoubleClick={() => handleOnDoubleClickItem(item.value)}
                  >
                    <div className={classes.NewTemplateText}>
                      <div>
                        {item.icon}
                        {item.label}
                      </div>
                      {currentFile &&
                        currentFile[0] &&
                        item.value ===
                          TEMPLATE_PDF_ACTIONS.LOAD_PDF_FROM_FILE.value && (
                          <div className={classes.FileName}>
                            <FontAwesomeIcon
                              icon={faFilePdf}
                              className={classes.FileNameIcon}
                            />
                            {currentFile[0].name}
                          </div>
                        )}
                    </div>
                    <div>
                      {currentTemplateAction === item.value && (
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className={classes.CheckedIcon}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="custom-file hidden">
                <input
                  ref={uploadRef}
                  type="file"
                  className="form-control"
                  accept="application/pdf,image/jpeg,image/png,image/tiff"
                  id="uploadPdf"
                  onChange={(e) => handleUploadedFile(e.target.files)}
                />
              </div>
            </>
          ) : (
            <>
              <div className={classes.SubTitle}>
                Create new empty template or load template from the library
              </div>

              <div className={classes.SearchWrap}>
                <input
                  type="text"
                  className="form-control"
                  id="aaa"
                  placeholder="...type to search"
                  onChange={(e) => onSearch(e.target.value)}
                />
              </div>

              <div className={classes.ListWrap}>
                <div
                  className={classNames(
                    classes.TemplateItem,
                    classes.NewTemplate,
                    {
                      [classes.ItemChecked]: checkedNewTemplate,
                    }
                  )}
                  onClick={handleChooseNewTemplate}
                  onDoubleClick={handleOnDoubleClickNewTemplateItem}
                >
                  <div className={classes.NewTemplateText}>
                    <FontAwesomeIcon
                      icon={faFileAlt}
                      className={classes.PrevIcon}
                    />
                    Create New Empty Template..
                  </div>
                  <div>
                    {checkedNewTemplate && (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className={classes.CheckedIcon}
                      />
                    )}

                    <FontAwesomeIcon icon={faFile} />
                  </div>
                </div>
                {currentList.map((option) => (
                  <div
                    className={classNames(classes.TemplateItem, {
                      [classes.ItemChecked]: isCheckedItem(option),
                    })}
                    onClick={(e) => handleChooseItem(option)}
                    onDoubleClick={() => handleOnDoubleClickItem(option)}
                    key={option.label}
                  >
                    <div>
                      <FontAwesomeIcon
                        icon={faBuilding}
                        className={classes.PrevIcon}
                      />
                      {option.label}
                    </div>
                    {isCheckedItem(option) && (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className={classes.CheckedIcon}
                      />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          <div className={classes.Footer}>
            <Button
              className={classNames("btn btn-lg", classes.Button)}
              variant="success"
              onClick={isLastStep ? onProceed : goToNextStep}
              disabled={!checkedItem && !checkedNewTemplate}
            >
              {isLastStep ? "Proceed.." : "Next"}
            </Button>
            <Button
              variant="secondary"
              onClick={handleCancel}
              className={classNames(
                "btn btn-lg",
                classes.CancelButton,
                classes.Button
              )}
            >
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

const mapStateToProps = (state, props) => {
  return {
    pdfFileName: state.pdfViewer.pdfFileName,
    isCustomFile: state.pdfViewer.isCustomFile,
    isTemplateModified: state.editor.isTemplateModified,
    hasTemplate: !isEmpty(state.editor.templateFileName),
  };
};

export default compose(
  withLoadData,
  connect(mapStateToProps, null)
)(SampleTemplatesModal);
